import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import socketService from '../api/socket';
import { useToast } from './ToastContext';
import queryClient from '../lib/queryClient';

const OrdersRealtimeContext = createContext(null);

const MAX_LIVE_ORDERS = 50;

/**
 * Écoute globalement l'événement socket "order:new" (room org:<organizationId>).
 *
 * Responsabilités GLOBALES (valables sur toutes les pages) :
 *  - Afficher un toast à chaque nouvelle commande
 *  - Incrémenter un compteur de commandes non vues (badge rouge sidebar)
 *  - Conserver les commandes reçues en temps réel pour que la page Commandes
 *    puisse les afficher en tête de liste sans recharger.
 *
 * La page Commandes appelle markSeen() à son ouverture pour remettre le badge à zéro.
 */
export function OrdersRealtimeProvider({ children }) {
  const toast = useToast();
  const [unseenCount, setUnseenCount] = useState(0);
  const [liveOrders, setLiveOrders] = useState([]);

  // toast est stable (ref), mais on le garde dans une ref pour ne pas
  // recréer le listener socket inutilement.
  const toastRef = useRef(toast);
  toastRef.current = toast;

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) return undefined;

    // Réutilise la connexion socket partagée (rejoint déjà la room org au connect)
    if (!socketService.isConnected()) {
      socketService.connect(token);
    }

    const handleOrderNew = (payload) => {
      if (!payload) return;
      const order = payload.data || payload;
      if (!order || !order._id) return;

      // Dédoublonnage : ignorer si déjà reçu
      setLiveOrders((prev) => {
        if (prev.some((o) => o._id === order._id)) return prev;
        return [order, ...prev].slice(0, MAX_LIVE_ORDERS);
      });
      setUnseenCount((c) => c + 1);

      // Met à jour le cache React Query directement (evite un re-fetch)
      queryClient.setQueryData(['orders'], (prev) => {
        if (!prev?.data) return prev;
        const already = Array.isArray(prev.data) && prev.data.some((o) => o._id === order._id);
        if (already) return prev;
        return { ...prev, data: [order, ...prev.data] };
      });

      const clientName = order.clientName || 'un client';
      toastRef.current.info(`Nouvelle commande de ${clientName}`, {
        title: '🛒 Nouvelle commande',
      });
    };

    socketService.on('order:new', handleOrderNew);
    return () => socketService.off('order:new', handleOrderNew);
  }, []);

  const markSeen = useCallback(() => setUnseenCount(0), []);

  // Permet à la page de retirer une commande live une fois qu'elle est
  // pleinement intégrée à la liste serveur (optionnel ; évite l'accumulation).
  const clearLiveOrders = useCallback(() => setLiveOrders([]), []);

  return (
    <OrdersRealtimeContext.Provider
      value={{ unseenCount, liveOrders, markSeen, clearLiveOrders }}
    >
      {children}
    </OrdersRealtimeContext.Provider>
  );
}

export function useOrdersRealtime() {
  const ctx = useContext(OrdersRealtimeContext);
  if (!ctx) {
    throw new Error(
      'useOrdersRealtime doit être utilisé à l’intérieur de <OrdersRealtimeProvider>'
    );
  }
  return ctx;
}

export default OrdersRealtimeContext;
